import { ErrorBoundary } from "react-error-boundary";

import GroupedLineChartWidget from "./GroupedLineChartWidget";
import ProgressBarChartWidget from "./ProgressBarChartWidget";
import GroupedBarChartWidget from "./GroupedBarChartWidget";
import FilledLineChartWidget from "./FilledLineChartWidget";
import StageFunnelWidget from "./StageFunnelWidget";
import SingleStatWidget from "./SingleStatWidget";
import { View } from "../../types/PageInterfaces";
import LineChartWidget from "./LineChartWidget";
import BarChartWidget from "./BarChartWidget";
import DocumentWidget from "./DocumentWidget";
import PieChartWidget from "./PieChartWidget";
import Constants from "../../utils/Constants";
import { SelectedChart } from "./WidgetTypes";
import TableWidgetV2 from "./TableWidgetV2";
import HeaderWidget from "./HeaderWidget";
import FlowWidget from "./FlowWidget";
import MapWidget from "./MapWidget";

const Themes = Constants.DASHBOARD_COLOR_THEMES;

interface Props {
  section: any;
  setSettings: any;
  useTableToolbar?: boolean;
  updateWidget: any;
  editMode: boolean;
  deleteWidget: any;
  record: any;
  theme: string | undefined;
  topLevelFilters?: any;
  onSelectGroup?: (chartGroup: SelectedChart | undefined) => void;
  selectedGroup?: SelectedChart;
  view?: View;
  pageID?: string;
  parentViewID?: string;
}

const WidgetRendererWrapper = (props: Props) => {
  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <div className="w-100 d-flex justify-content-center align-items-center h-100 text-danger font-weight-600">
          Widget Error
        </div>
      )}
    >
      <WidgetRenderer {...props} />
    </ErrorBoundary>
  );
};

const WidgetRenderer = ({
  section,
  setSettings = null,
  useTableToolbar = true,
  updateWidget,
  editMode,
  deleteWidget,
  record,
  theme,
  topLevelFilters,
  onSelectGroup,
  selectedGroup,
  view,
  pageID,
  parentViewID,
}: Props): JSX.Element => {
  const viewFilters = view?.ViewFilters?.Filters;
  const finalTheme = Themes[theme || ""];

  switch (section?.Type) {
    case "Header":
      return (
        <HeaderWidget
          section={section}
          setSettings={setSettings}
          editMode={editMode}
          deleteWidget={deleteWidget}
          record={record}
        />
      );
    case "Table":
      return (
        <TableWidgetV2
          section={section}
          setSettings={setSettings}
          useToolbar={useTableToolbar}
          updateWidget={updateWidget}
          record={record}
        />
      );
    case "Document":
      return <DocumentWidget section={section} setSettings={setSettings} />;
    case "PieChart":
    case "DonutChart":
      return (
        <PieChartWidget
          section={section}
          setSettings={setSettings}
          record={record}
          theme={finalTheme}
          topLevelFilters={topLevelFilters}
          viewFilters={viewFilters}
          onSelectGroup={onSelectGroup}
          selectedGroup={selectedGroup}
        />
      );
    case "BarChart":
      return (
        <BarChartWidget
          section={section}
          setSettings={setSettings}
          record={record}
          theme={finalTheme}
          topLevelFilters={topLevelFilters}
          viewFilters={viewFilters}
          onSelectGroup={onSelectGroup}
          selectedGroup={selectedGroup}
        />
      );
    case "GroupedBarChart":
      return (
        <GroupedBarChartWidget
          section={section}
          setSettings={setSettings}
          theme={finalTheme}
          topLevelFilters={topLevelFilters}
          record={record}
          viewFilters={viewFilters}
          onSelectGroup={onSelectGroup}
          selectedGroup={selectedGroup}
        />
      );
    case "ProgressBarChart":
      return (
        <ProgressBarChartWidget
          section={section}
          setSettings={setSettings}
          theme={finalTheme}
          topLevelFilters={topLevelFilters}
          record={record}
          viewFilters={viewFilters}
        />
      );
    case "LineChart":
      return (
        <LineChartWidget
          section={section}
          setSettings={setSettings}
          record={record}
          theme={finalTheme}
          topLevelFilters={topLevelFilters}
          viewFilters={viewFilters}
        />
      );
    case "GroupedLineChart":
      return (
        <GroupedLineChartWidget
          section={section}
          setSettings={setSettings}
          theme={finalTheme}
          topLevelFilters={topLevelFilters}
          record={record}
          viewFilters={viewFilters}
        />
      );
    case "FilledLineChart":
      return (
        <FilledLineChartWidget
          section={section}
          setSettings={setSettings}
          record={record}
          theme={finalTheme}
          topLevelFilters={topLevelFilters}
          viewFilters={viewFilters}
        />
      );
    case "SingleStat":
      return (
        <SingleStatWidget
          section={section}
          setSettings={setSettings}
          record={record}
          topLevelFilters={topLevelFilters}
          viewFilters={viewFilters}
        />
      );
    case "StageFunnel":
      return (
        <StageFunnelWidget
          section={section}
          setSettings={setSettings}
          record={record}
          theme={finalTheme}
          topLevelFilters={topLevelFilters}
          viewFilters={viewFilters}
          onSelectGroup={onSelectGroup}
          selectedGroup={selectedGroup}
        />
      );
    case "FlowChart":
      return (
        <FlowWidget
          section={section}
          setSettings={setSettings}
          view={view}
          pageID={pageID}
          parentViewID={parentViewID}
          drawerRecord={record}
        />
      );

    case "Map":
      return (
        <MapWidget
          section={section}
          setSettings={setSettings}
          theme={finalTheme}
          topLevelFilters={topLevelFilters}
          viewFilters={viewFilters}
          record={record}
          onSelectGroup={onSelectGroup}
          selectedGroup={selectedGroup}
        />
      );
    default:
      return <>None</>;
  }
};

export default WidgetRendererWrapper;
